import { AsyncValidatorFn, FormControl, FormControlOptions, FormGroup, ValidatorFn } from '@angular/forms';

import { KeyValueDescriptionType } from '@models/common';
import { DisplayType } from '@models/module/fields/enums';
import {
  AddressModel,
  AttachmentModel,
  BaseFieldValueType,
  FormFieldValueType,
  MimeTypesEnum,
} from '@models/module/fields/main';
import { Currency } from '@models/system';
import { AppColors } from '@shared/consts';

type CipoFieldBaseConfig = {
  type?: CipoFieldTypes;
  /** The display sub-type for this field, maps to the backend `DisplayType` enum. */
  displayType?: DisplayType;
  /* for dynamic fields */
  id?: number;
  contractId?: number;
  moduleId?: number;
  entityInstanceId?: number;
  label?: string;
  name?: string;
  systemFieldName?: string;
  placeholder?: string;
  hint?: string;
  fieldDescription?: string;
  /* Used to hide the underline in read-only mode. May be also used on enabled fields if implemented (only for appearanceFill) */
  cleanReadOnly?: boolean;
  readonly?: boolean;
  required?: boolean;
  prefix?: string;
  prefixIcon?: string;
  suffix?: string;
  suffixIcon?: string;
  preventClearInput?: boolean;
  /** HTML autocomplete token (e.g. "email", "name", "tel"). Passed as [attr.autocomplete] on the native input. */
  autocomplete?: string;
  appearanceFill?: boolean;
  optionViewType?: CipoListOptionViewType;
  markChanged?: boolean;
  /** Optional data-testid for e2e tests; applied on the field component host/wrapper. */
  testId?: string;
  position?: {
    x: number;
    y: number;
    cols: number;
    rows: number;
  };
  /* The field is in draft mode or is part of a draft document */
  isDraft?: boolean;
};

export type CipoTextConfig = CipoFieldBaseConfig & {
  maxLength?: number;
  /** Maximum visible rows before a multiline field starts scrolling. */
  maxRows?: number;
  multiple?: boolean;
  multiline?: boolean;
  focused?: boolean;
};

export type CipoNumberConfig = CipoFieldBaseConfig & {
  showThousandSeparator?: boolean;
  min?: number;
  max?: number;
  decimals?: number;
  multiple?: boolean;
  /** This field holds the multiple property for multiline / richtext fields. Bu default we should not have multiple multiline field, but BE still expects it.
   * To be removed when form definition is implemented
   */
  hasMultipleValues?: boolean;
  /** @Important If you use the separate fieldData input
   * and plan to update the fieldData after init, make sure to send fieldData as a model - [(fieldData)]="currencyFieldData"
   *
   * This is mandatory because cipo-number updates the prefix / suffix based on currency. */
  currency?: Currency;
  focused?: boolean;
};

// TODO: change options type to allow number key. Avoid any.
export type CipoListConfig = CipoFieldBaseConfig & {
  /** To be used when the options are available at creation time */
  options?: KeyValueDescriptionType<any>[];
  /**
   * To be used when the options are from a dictionary (also redux, after implementing signalR)
   *
   * To reload the options - make options: null */
  dataSourceInfo?: DataSourceInfoModel;
  /** Do not show 'All options selected' in the form field */
  preventAllSelectedLabel?: boolean;
  /** All options selected label */
  allSelectedLabel?: string;
  /** hide the first option (none / all) which is usually displayed. Only available for single select */
  hideNullOption?: boolean;
  //   we might need to add a 'getOptions' method
  columns?: number;
  multiple?: boolean;
  disabled?: boolean;
  hideSearch?: boolean;
  noneLabel?: string;
  canSelectNullableOptions?: boolean;
  /** Allow user to type new values. Only for cipo-autocomplete */
  allowNewValues?: boolean;
  allowDragAndDrop?: boolean;
  /** Show a tri-state select-all toggle button outside the dropdown as a suffix icon. Hides the select/unselect-all option inside the dropdown. Only applies to multiple selects. */
  toggleAllOutside?: boolean;
  hideInUse?: boolean;
};

export type RelatedListParent = {
  parentFieldId: number;
  parentElementIds: string[];
};

/* TODO: remove deprecated properties */
export type DataSourceInfoModel = {
  id: number;
  relationId?: number;
  /** @deprecated use config.contractId instead. By default it takes the current contract (from redux ) */
  contractId?: number;
  /** When the datasource is different for each module */
  moduleId?: number;
  /** @deprecated use config.entityInstanceId instead */
  entityInstanceId?: number;
  /** @deprecated use config.id instead */
  fieldId?: number;

  /* Used for relation data source */
  selectedIds?: string[];

  /* Used for related fields */
  parents?: RelatedListParent[];
  childFieldIds?: number[];

  /* Used for SOV */
  sovEntityInstanceId?: number;
};

export type CipoCheckboxConfig = CipoFieldBaseConfig;

export type CipoDateTimeSelectMode = 'single' | 'range';
export type CipoDateTimePickerType = 'dateTime' | 'date' | 'time';

export type CipoWeatherTableFieldValueType = { [key: number]: string | null };

export type CipoDateTimeConfig = CipoFieldBaseConfig & {
  minDate?: string;
  maxDate?: string;
  maxRange?: number;
  hour12Timer?: boolean;
  selectMode?: CipoDateTimeSelectMode;
  pickerType?: CipoDateTimePickerType;
};

export type CipoAttachmentConfig = CipoFieldBaseConfig & {
  multiple?: boolean;
  maxFileSize?: number;
  allowedMimeTypes?: MimeTypesEnum[];
  showOnlyAttachmentByIndex?: number;
  bgColor?: AppColors;
};

export type CipoTableConfig = Pick<
  CipoFieldBaseConfig,
  | 'fieldDescription'
  | 'required'
  | 'type'
  | 'readonly'
  | 'label'
  | 'id'
  | 'entityInstanceId'
  | 'markChanged'
  | 'isDraft'
> & {
  aggregateValue?: Record<number, string>;
  /* used to get table fields */
  showPosition?: boolean;
};

export type CipoWeatherTableConfig = CipoTableConfig & {
  actionInstanceId?: number;
  weatherDateId?: number;
  weatherLocationId?: number;
};
export type CipoAddressConfig = CipoFieldBaseConfig & {
  multiple?: boolean;
};

/** Union of all possible configs */
export type CipoFieldConfig = CipoTextConfig &
  CipoNumberConfig &
  CipoDateTimeConfig &
  CipoListConfig &
  CipoCheckboxConfig &
  CipoAttachmentConfig &
  CipoTableConfig &
  CipoWeatherTableConfig &
  CipoAddressConfig;

export enum CipoFieldTypes {
  Text = 'Text',
  Number = 'Number',
  DateTime = 'DateTime',
  Select = 'Select',
  Listbox = 'Listbox',
  Checkbox = 'Checkbox',
  Attachment = 'Attachment',
  RichText = 'RichText',
  Annotation = 'Annotation',
  Table = 'Table',
  Weather = 'Weather',
  Address = 'Address',
  RadioGroup = 'RadioGroup',
}

export enum CipoListOptionViewType {
  FLAG = 'flag',
}

export interface FormItem<TValue = CipoFieldValue, TConfig = CipoFieldConfig> {
  id: string;
  value?: TValue;
  fieldOptions?: TConfig;
}

export type CipoFieldValue = FormFieldValueType; //string | number | boolean | string[] | number[] | AttachmentModel[];
export type CipoFormGroup<TValue = CipoFieldValue, TField = CipoFieldConfig> = FormGroup<{
  [key: string]: CipoControl<TValue, TField>;
}>;

export class CipoControl<TValue = CipoFieldValue, TField = CipoFieldConfig> extends FormControl<TValue> {
  fieldData?: TField;

  constructor(
    formState: TValue,
    fieldData?: TField,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(formState, validatorOrOpts, asyncValidator);
    this.fieldData = fieldData;
  }
}

export class CipoTextControl extends CipoControl<string | string[], CipoTextConfig> {}

export class CipoNumberControl extends CipoControl<number | number[], CipoNumberConfig> {}

export class CipoDateTimeControl extends CipoControl<string | string[], CipoDateTimeConfig> {
  constructor(
    formState: string | string[],
    fieldData?: CipoDateTimeConfig,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(
      formState,
      { type: CipoFieldTypes.DateTime, selectMode: 'single', pickerType: 'dateTime', ...fieldData },
      validatorOrOpts,
      asyncValidator,
    );
  }
}

export class CipoDateControl extends CipoControl<string | string[], CipoDateTimeConfig> {
  constructor(
    formState: string | string[],
    fieldData?: CipoDateTimeConfig,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(
      formState,
      { type: CipoFieldTypes.DateTime, selectMode: 'single', pickerType: 'date', ...fieldData },
      validatorOrOpts,
      asyncValidator,
    );
  }
}

export class CipoTimeControl extends CipoControl<string | string[], CipoDateTimeConfig> {
  constructor(
    formState: string | string[],
    fieldData?: CipoDateTimeConfig,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(
      formState,
      { type: CipoFieldTypes.DateTime, selectMode: 'single', pickerType: 'time', ...fieldData },
      validatorOrOpts,
      asyncValidator,
    );
  }
}

// TODO: check above from CipoListConfig
export class CipoListControl extends CipoControl<any, CipoListConfig> {}

export class CipoCheckboxControl extends CipoControl<boolean, CipoCheckboxConfig> {
  constructor(
    formState: boolean,
    fieldData?: CipoCheckboxConfig,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(formState, { type: CipoFieldTypes.Checkbox, ...fieldData }, validatorOrOpts, asyncValidator);
  }
}

export class CipoAttachmentControl extends CipoControl<AttachmentModel[] | AttachmentModel, CipoAttachmentConfig> {}

export class CipoAddressControl extends CipoControl<AddressModel | AddressModel[], CipoAddressConfig> {}

export type CipoForm = FormGroup<{ [x: string]: CipoControl }>;

export type CipoTextViewConfig = {
  oldValue?: BaseFieldValueType;
  newValue?: BaseFieldValueType;
  ellipsed?: boolean;
  bgColor?: AppColors;
};
